import {useCallback, useMemo, useState} from 'react';

import {AuditLogsDataResponse, getAuditLogsData} from 'audit/AuditDAO';
import useOnMount from 'ui-library/hooks/useOnMount';

interface UseAuditLogDataProps {
  ids: Array<string>;
  pageSize: number;
  resultsWarehouseIds?: Array<string>;
  relatedResultsModelId?: string;
}

interface AuditLogDataState {
  isLoading: boolean;
  auditData: AuditLogsDataResponse | null;
  currentPage: number;
  error: string | null;
}

interface UseAuditLogDataResult {
  state: AuditLogDataState;
  allColumns: Array<string>;
  loadAuditData: (page?: number) => void;
}

export default function useAuditLogData({
  ids,
  pageSize,
  resultsWarehouseIds,
  relatedResultsModelId,
}: UseAuditLogDataProps): UseAuditLogDataResult {
  const [state, setState] = useState<AuditLogDataState>({
    isLoading: true,
    auditData: null,
    currentPage: 0,
    error: null,
  });
  const allColumns = useMemo(() => state.auditData?.columns || [], [state.auditData]);

  const loadAuditData = useCallback(
    async (page: number = 0) => {
      setState((prev) => ({...prev, isLoading: true, error: null}));

      try {
        const response: AuditLogsDataResponse = await getAuditLogsData(ids, {
          resultsWarehouseIds,
          relatedResultsModelId,
          pageSize,
          pageOffset: page,
        });

        setState((prev) => ({
          ...prev,
          isLoading: false,
          auditData: response,
          currentPage: page,
        }));
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: 'Failed to load audit log data. Please try again.',
        }));
      }
    },
    [ids, pageSize, resultsWarehouseIds, relatedResultsModelId]
  );

  // Initial load
  useOnMount(() => {
    loadAuditData(0);
  });

  return {
    state,
    allColumns,
    loadAuditData,
  };
}
